<template>
  <div :class="$style.mainArea">
    <form :class="$style.wrapper">
      <div :class="$style.builder">
        <h1
          :class="$style.headline"
          :style="{color: division.accent}">GO2 Signature Builder</h1>
        <div :class="$style.builderForm">
          <InputField
            :autocomplete="'given-name'"
            :field="'firstName'"
            :validator="validator"
            v-model="formData.firstName"
            placeholder=""/>
          <InputField
            :autocomplete="'family-name'"
            :field="'lastName'"
            :validator="validator"
            v-model="formData.lastName"
            placeholder=""/>
          <SelectField
            :field="'pronoun'"
            :options="pronouns"
            :validator="validator"
            v-model="formData.pronoun"
            autocomplete="off"
            placeholder=""/>
          <InputField
            :autocomplete="'organization-title'"
            :field="'title'"
            :validator="validator"
            v-model="formData.title"
            placeholder=""/>
          <InputField
            :autocomplete="'email'"
            :field="'email'"
            :validator="validator"
            v-model="formData.email"
            placeholder=""/>
          <InputField
            :field="'meetingLink'"
            :validator="validator"
            v-model="formData.meetingLink"
            autocomplete="off"
            placeholder=""/>
          <SelectField
            :field="'office'"
            :options="offices"
            :validator="validator"
            v-model="formData.office"
            autocomplete="off"
            placeholder=""/>
          <MultiSelect
            :field="'socials'"
            :options="socialOptions"
            :validator="validator"
            v-model="formData.socials"
            autocomplete="off"
            placeholder=""
            @checked="updateSocials"
          />
        </div>
        <Btn
          :class="$style.phoneBtn"
          @click.native="addPhone">Add Phone
        </Btn>
        <template v-if="hasPhone">
          <NewPhone
            v-for="(phone, index) in formData.phones"
            :accent="division.accent"
            :form-validator="validator"
            :index="index"
            :key="index"
            :phone="phone"
            @remove="removePhone"/>
        </template>
        <Btn
          :class="$style.copyBtn"
          @click.native="copySig">Copy Signature
        </Btn>
        <!--
        <Signature
          ref="signature"
          :division="division"
          :has-phone="hasPhone"
          :person="formData"/>
        -->
        <HtmlSignature
          ref="signature"
          :division="division"
          :has-phone="hasPhone"
          :person="formData"/>
      </div>
      <div :class="$style.instructions">
        <Instruction
          :accent="division.accent"
          :step-number="1"
          @location="placeBg">Enter your information in the form.
        </Instruction>
        <Instruction
          :accent="division.accent"
          :step-number="2">Click here to copy your signature, or use the button to the left.<br/><br/>
          <Btn @click.native="copySig">Copy Signature</Btn><br/>
          <!--<Btn @click.native="copySigHtml">Copy HTML Signature</Btn><br/>-->
        </Instruction>
        <Instruction
          :accent="division.accent"
          :step-number="3">Go to Outlook > File > Options > Mail > Signatures. Create a New signature and then Paste your information into the blank white box and hit OK.
        </Instruction>
      </div>
    </form>
    <div
      id="landscape"
      :class="$style.landscape"
      :style="{
        background: `url(${imageUrl})`,
        backgroundSize: 'cover',
        left: imageLeft,
        width: imageWidth,
        height: imageHeight}"/>
  </div>
</template>

<script>
/* Meteor packages */
/* NPM packages */
import copy from 'copy-html-to-clipboard';

/* Vue Components */
import Btn from '../components/button/Btn.vue';
import InputField from '../components/formFields/FormField.Input.vue';
import MultiSelect from '../components/formFields/FormField.MultiSelect.vue';
import SelectField from '../components/formFields/FormField.Select.vue';
import Instruction from '../components/instruction/Instruction.vue';
import NewPhone from '../components/newPhone/NewPhone.vue';
import Signature from '../components/signature/Signature.vue';

import HtmlSignature from '../components/signature/HtmlSignature.vue';

//import offices from 'src/data/offices';
import schema from '../schemas/builder';
/* Methods */
/* Everything else */
import divisions from '../data/data';
import offices from '../data/offices';
import pronouns from '../data/pronouns';
import socialOptions from '../data/socialOptions';

export default {
  name: 'Builder',
  components: {
    Btn,
    InputField,
    Instruction,
    MultiSelect,
    NewPhone,
    SelectField,
    Signature,
    HtmlSignature,
  },
  props: {
    divisionName: {
      type: String,
      default: null,
    },
  },
  data() {
    const validator = schema.newContext();
    validator.validationTrigger = 0;
    return {
      validator,
      divisions,
      offices,
      pronouns,
      socialOptions,
      division: null,
      imageLeft: 0,
      imageWidth: 0,
      imageHeight: 0,
      formData: {
        firstName: '',
        lastName: '',
        pronoun: '',
        title: '',
        email: '',
        meetingLink: '',
        phones: [],
        office: '',
        socials: [],
      },
      hasPhone: false,
    };
  },
  computed: {
    imageUrl() {
      return `${location.href.replace(/#.*/, '')}images/${
        this.division.image
      }`;
    },
    completeEmail() {
      return `${this.formData.firstName}.${this.formData.lastName}`;
    },
  },
  watch: {
    completeEmail(value) {
      this.formData.email = `${value.toLowerCase()}@${this.division.emailDomain}`;
    },
  },
  created() {
    this.division = divisions.find(
      (division) => division.name === this.divisionName,
    );
  },
  mounted() {
    this.division = divisions.find(
      (division) => division.name === this.divisionName,
    );
  },
  updated() {
    const elHeight = document.body.offsetHeight - 120;
    this.imageHeight = `${elHeight}px`;
  },
  methods: {
    placeBg(offsetLeft) {
      const elWidth = document.body.offsetWidth - offsetLeft;
      const elHeight = document.body.offsetHeight - 120;
      this.imageLeft = `${offsetLeft}px`;
      this.imageWidth = `${elWidth}px`;
      this.imageHeight = `${elHeight}px`;
    },
    async copySig() {
      if (this.validator.validate(this.formData)) {
        await this.copyToClipboard(this.$refs.signature.$el.innerHTML, 'both');
        alert('Your signature has been copied to the clipboard');
      } else {
        this.validator.validationTrigger++;
      }
    },
    async copySigHtml() {
      if (this.validator.validate(this.formData)) {
        //copy2(this.$refs.signature.$el.outerHTML, {
        //  debug: true,
        //  format: 'text/plain',
        //});
        await this.copyToClipboard(this.$refs.signature.$el.innerHTML, 'text');
        alert('Your signature has been copied to the clipboard');
      } else {
        this.validator.validationTrigger++;
      }
    },
    async copyToClipboard(value, format) {
      let data = [];
      const typeRich = 'text/html';
      const blobRich = new Blob([value], { type: typeRich });

      const typePlain = 'text/plain';
      const blobPlain = new Blob([value], { type: typePlain });

      // Copy both types so it can be pasted into either a Rich Text editor with formatting,
      // or a plain text editor with basic formatting (e.g., new lines but not HTML tags)
      if (format === 'both') {
        data = [
          new window.ClipboardItem({
            [typeRich]: blobRich,
            [typePlain]: blobPlain,
          }),
        ];
      } else if (format === 'text') {
        data = [
          new window.ClipboardItem({
            [typePlain]: blobPlain,
          }),
        ];
      } else {
        data = [
          new window.ClipboardItem({
            [typeRich]: blobRich,
          }),
        ];
      }

      await navigator.clipboard.write(data);
    },

    addPhone() {
      this.formData.phones.push({
        phoneType: '',
        phoneNumber: '',
      });
      this.hasPhone = true;
    },
    removePhone(index) {
      this.formData.phones.splice(index, 1);
    },
    updateSocials(socials) {
      this.formData.socials = socials;
    },
  },
};
</script>
<style lang="scss" module>
@import 'src/brand/brandColors.variables';

.mainArea {
  composes: mainContent from '../brand/structure.scss';
  display: flex;
  justify-content: space-between;
  position: relative;
}

.wrapper {
  composes: pageContainer from 'src/brand/structure.scss';
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.builder {
  flex: 2;
  margin-bottom: 4.8rem;
}

.headline {
  composes: standard from '../brand/typography.scss';
  font-size: 3.6rem;
  width: 100%;
  text-align: left;
  margin: 4.8rem auto;
}

.builderForm {
  display: flex;
  flex-flow: row wrap;
}

.phoneBtn {
  margin-bottom: 1.6rem;
}

.copyBtn {
  margin-top: 9.8rem;
  margin-bottom: 2.4rem;
}

.instructions {
  flex: 1;
  padding-top: 4.8rem;
}

.landscape {
  position: absolute;
  z-index: -1;
  background-size: cover;
}
</style>
